import { ApiError } from '@/api/client'
import { tokens } from '@/theme'

interface Props {
  /** Error to render. ApiError surfaces code + traceId; Error renders just the message. */
  error: Error
  /** Optional max width — defaults to 480 (matches list rows). */
  maxWidth?: number | string
}

/**
 * Standard error panel for any failed read/write.
 *
 * Shows the API's stable error code, the human message, and the traceId
 * (selectable for paste into a support ticket). For non-ApiError values
 * we fall back to "ERROR" + message.
 *
 * 403s get a stale-token hint. App Role assignments are baked into the
 * token at issue time, so a freshly-assigned user keeps seeing 403 until
 * they sign out and back in. Surfacing the hint inline avoids a support
 * round-trip for the common case.
 */
export function ErrorPanel({ error, maxWidth = 480 }: Props) {
  const isApi = error instanceof ApiError
  const code = isApi ? error.code : 'ERROR'
  const traceId = isApi ? error.traceId : null
  const isForbidden = isApi && error.status === 403

  return (
    <div
      role="alert"
      style={{
        background: tokens.statusRedWash,
        border: `1px solid ${tokens.statusRedLine}`,
        borderRadius: tokens.radiusMd,
        padding: '16px 20px',
        maxWidth,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: tokens.weightDemi,
          color: tokens.statusRed,
          letterSpacing: '0.08em',
          marginBottom: 6,
        }}
      >
        {code}
      </div>
      <div style={{ fontSize: 14, color: tokens.ink2 }}>{error.message}</div>
      {isForbidden && (
        <div style={{ fontSize: 12, color: tokens.ink2, marginTop: 10, lineHeight: 1.5 }}>
          If your access was recently changed, your sign-in token may be stale. Sign out and sign
          back in to refresh it.
        </div>
      )}
      {traceId && (
        <div style={{ fontSize: 11, color: tokens.ink4, marginTop: 8 }}>
          Trace ID:{' '}
          <code style={{ userSelect: 'all', fontFamily: 'ui-monospace, monospace' }}>
            {traceId}
          </code>
        </div>
      )}
    </div>
  )
}
